import React, { useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Users, Maximize, Check, BedDouble, Coffee, Sun } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import PublicLayout from '../../components/site/PublicLayout';
import PageBanner from '../../components/site/PageBanner';
import RoomGallery from '../../components/site/RoomGallery';
import RoomPriceCard from '../../components/site/RoomPriceCard';
import OtherRooms from '../../components/site/OtherRooms';
import FaqAccordion, { faqNodes } from '../../components/site/FaqAccordion';
import { useLanguage } from '../../context/LanguageContext';
import { useRooms } from '../../hooks/useRooms';
import { localizeRoom, amenityLabel } from '../../i18n/roomsI18n';
import { SLUG_TO_ROOM, roomPageCopy } from '../../i18n/roomPages';
import { langPath } from '../../utils/siteNav';
import { absUrl, ROOM_BEDS } from '../../seo/hotelFacts';
import { roomNode, offerNode } from '../../seo/buildSchema';
import { truncate } from '../../seo/pageMeta';
import { trackRoomView } from '../../hooks/useAnalytics';

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
    <Icon className="w-5 h-5 text-amber-600 mt-0.5" />
    <div><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className="text-slate-900 font-medium mt-0.5">{value}</p></div>
  </div>
);

// Dedicated, indexable page per room type: photos, facts, amenities, FAQ, live Beds24 price and HotelRoom/Offer/FAQPage JSON-LD.
export default function RoomPage() {
  const { roomSlug } = useParams();
  const { language, t } = useLanguage();
  const allRooms = useRooms();
  const beds24Id = SLUG_TO_ROOM[roomSlug];
  const raw = allRooms.find((r) => (r.beds24Id || r.id) === beds24Id);
  const room = useMemo(() => (raw ? localizeRoom(raw, language, t) : null), [raw, language, t]);
  const page = `rooms/${roomSlug}`;

  useEffect(() => { if (beds24Id) trackRoomView(beds24Id); }, [beds24Id]);

  const copy = useMemo(() => (room ? roomPageCopy(language, room) : null), [language, room]);
  const meta = useMemo(() => (room && copy ? {
    title: `${room.name} — ${copy.labels.metaFrom} €${Math.round(room.price)} ${copy.labels.perNight} | Hotel Uma Premium Ulcinj`,
    description: truncate(room.description),
    image: room.images?.[0],
  } : null), [room, copy]);
  const schema = useMemo(() => {
    if (!room || !copy) return null;
    const url = absUrl(language, page);
    return {
      pageType: 'WebPage',
      image: room.images?.[0],
      mainEntity: { ...roomNode(room, language, t), offers: offerNode(room, language, t, url) },
      crumbs: [{ name: copy.labels.rooms, item: absUrl(language, 'rooms') }, { name: room.name, item: url }],
      nodes: [{ '@type': 'FAQPage', '@id': `${url}#faq`, mainEntity: faqNodes(copy.faq) }],
    };
  }, [room, copy, language, page, t]);

  if (!beds24Id) return <Navigate to={langPath(language, 'rooms')} replace />;
  if (!room) return <PublicLayout><div className="min-h-[60vh]" /></PublicLayout>;

  const { labels, faq } = copy;
  const others = allRooms.filter((r) => (r.beds24Id || r.id) !== beds24Id).map((r) => localizeRoom(r, language, t));
  const bed = ROOM_BEDS[beds24Id];

  return (
    <PublicLayout>
      <SEOHead page={page} meta={meta} schema={schema} />
      <PageBanner slug="rooms" title={room.name} image={room.images?.[0]} compact crumbs={[{ label: labels.rooms, to: langPath(language, 'rooms') }]}
        subtitle={`${room.size} · ${room.guests} ${labels.guests.toLowerCase()} · ${labels.from} €${Math.round(room.price)} ${labels.perNight}`} />
      <main data-testid={`page-room-${roomSlug}`}>
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-10 lg:gap-14 items-start">
          <div className="space-y-14 min-w-0">
            <RoomGallery images={room.images || []} alt={room.name} label={labels.photos} />

            <section aria-labelledby="room-about">
              <h2 id="room-about" className="font-serif text-2xl md:text-3xl text-slate-900">{labels.about}</h2>
              <p className="mt-4 text-slate-700 leading-relaxed text-base" data-testid="room-description">{room.description}</p>
            </section>

            <section aria-labelledby="room-facts">
              <h2 id="room-facts" className="font-serif text-2xl md:text-3xl text-slate-900 mb-5">{labels.facts}</h2>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="room-facts">
                <Fact icon={Maximize} label={labels.size} value={room.size} />
                <Fact icon={Users} label={labels.guests} value={`1–${room.guests}`} />
                <Fact icon={bed ? BedDouble : Sun} label={bed ? amenityLabel('Balcony', t) : labels.balcony} value={bed || labels.balcony} />
                <Fact icon={Coffee} label={t('nav.amenities')} value={labels.breakfast} />
              </div>
            </section>

            <section aria-labelledby="room-amenities">
              <h2 id="room-amenities" className="font-serif text-2xl md:text-3xl text-slate-900 mb-5">{labels.amenities}</h2>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5" data-testid="room-amenities">
                {(room.amenities || []).map((a) => (
                  <li key={a} className="flex items-center gap-2 text-slate-700 text-sm"><Check className="w-4 h-4 text-amber-600 flex-shrink-0" />{amenityLabel(a, t)}</li>
                ))}
              </ul>
            </section>

            <FaqAccordion items={faq} title={labels.faq} id="room-faq" />
          </div>

          <RoomPriceCard room={room} labels={labels} />
        </div>
        <OtherRooms rooms={others} labels={labels} />
      </main>
    </PublicLayout>
  );
}
